import { cn } from '@/shared/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import * as React from 'react';

/**
 * Figma 버튼 규격
 * - primary: 검정 채움 (등록하기 · 로그인)
 * - secondary: 회색 채움 (전체 처방 · 정산 내역, 전화, 상세)
 * - navy: 문전약국
 * - outline: 흰 배경 + 테두리 (연도 select 등)
 * - ghost: 텍스트 링크형
 */
const buttonVariants = cva(
  'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap font-medium transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary/40 disabled:pointer-events-none disabled:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary: 'bg-ink text-white hover:bg-ink/85',
        secondary: 'bg-muted text-ink-2 hover:bg-[#e8e8e8]',
        navy: 'bg-navy text-white hover:bg-navy/90',
        outline:
          'border border-line-strong bg-surface text-ink hover:bg-subtle',
        ghost: 'text-ink-3 hover:bg-muted',
        link: 'h-auto px-0 text-primary hover:underline',
      },
      size: {
        sm: 'h-[34px] rounded-sm px-3 text-xs',
        md: 'h-[38px] rounded-md px-4 text-xs',
        lg: 'h-11 rounded-md px-5 text-[13px]',
        xl: 'h-12 rounded-lg px-6 text-sm',
        icon: 'size-9 rounded-md',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'lg',
    },
  },
);

export interface ButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      loading,
      children,
      disabled,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        disabled={disabled || loading}
        {...props}
      >
        {asChild ? (
          children
        ) : (
          <>
            {loading && <Loader2 className="animate-spin" aria-hidden />}
            {children}
          </>
        )}
      </Comp>
    );
  },
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
